// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInButton from "@keller/App/Components/Common/SignInButton";
import AddToCartVariantParent from "@keller/App/Components/Product/AddToCartVariantParent";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import ProductAddToCartButton from "@keller/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink from "@keller/Overrides/Components/ProductAddToListLink";
import ProductQuantityOrdered, {
    ProductQuantityOrderedActionsStyles,
} from "@keller/Overrides/Components/ProductQuantityOrdered";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

interface OwnProps {}

export type Props = OwnProps & HasProductContext;

interface AddToCartActionsSection {
    signInWrapper?: InjectableCss;
    signInButton?: ButtonPresentationProps;
    signInVariantTypography?: TypographyPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: ProductQuantityOrderedActionsStyles;
    addToListLink?: ProductAddToListLinkStyles;
}

const addToCartActionsSection: AddToCartActionsSection = {
    signInWrapper: {
        css: css`
            width: 100%;
        `,
    },
    signInButton: {
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            width: 100%;
            font-size: 10px;
        `,
    },
    signInVariantTypography: {
        css: css`
            font-size: 12px;
            line-height: 18px;
            margin-bottom: 20px;
        `,
    },
    addToCartButton: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
            font-size: 10px;
            width: 100%;
            margin-bottom: 8px;
        `,
    },
    quantityOrdered: {
        wrapper: {
            css: css`
                margin-right: 20px;
            `,
        },
    },
};

const styles = addToCartActionsSection;

const AddToCartActionSection: FC<Props> = ({ productContext }) => {
    const { isAuthenticated, isNonPurchasable } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        isNonPurchasable: getIsNonPurchasable(state),
    }));

    const dispatch = useAppDispatch();
    const history = useHistory();
    const { product } = productContext;
    const isVariantProduct = product.isVariantParent;
    const productDetailPath = productContext.productInfo.productDetailPath || product.uri;

    // Read variant selection for this parent product
    const variantSelection = useAppSelector(state =>
        isVariantProduct
            ? ((state as any).xProductListVariants?.variantSelectionsByProductId?.[product.id] ?? {})
            : {},
    );

    // Read loaded variant children
    const variantChildren = useAppSelector(state =>
        getVariantChildrenDataView(state, isVariantProduct ? product.id : undefined).value,
    );

    // Check if all variant traits have a selected value
    const isSelectionComplete = useMemo(() => {
        if (!isVariantProduct || !product.variantTraits || product.variantTraits.length === 0) {
            return true;
        }
        return product.variantTraits.every(trait => !!variantSelection[trait.id]);
    }, [isVariantProduct, product.variantTraits, variantSelection]);

    // Find the matching variant product
    const selectedVariant: ProductModel | undefined = useMemo(() => {
        if (!isSelectionComplete || !variantChildren) {
            return undefined;
        }
        return variantChildren.find(child =>
            child.childTraitValues?.every(ctv => variantSelection[ctv.styleTraitId] === ctv.id),
        );
    }, [isSelectionComplete, variantChildren, variantSelection]);

    // PDP URL with ?option= pre-selects the variant on the product detail page
    const productDetailPathWithVariant = useMemo(() => {
        if (selectedVariant?.productNumber) {
            return `${productDetailPath}?option=${encodeURIComponent(selectedVariant.productNumber)}`;
        }
        return productDetailPath;
    }, [productDetailPath, selectedVariant]);

    // Build a ProductContextModel for the selected variant to pass to Add to Cart
    const variantProductContext: ProductContextModel | undefined = useMemo(() => {
        if (!selectedVariant) {
            return undefined;
        }
        const variantProductInfo = {
            ...createFromProduct(selectedVariant),
            // Carry over qty from parent so the qty input is respected
            qtyOrdered: productContext.productInfo?.qtyOrdered ?? 1,
            unitOfMeasure: productContext.productInfo?.unitOfMeasure ?? "",
        };
        return {
            product: selectedVariant,
            productInfo: variantProductInfo,
            idx: productContext.idx,
            onQtyOrderedChanged: (qty: number) => {
                // Write qty back to the parent product info slot (variant has no separate slot)
                dispatch({ type: "Pages/ProductList/ChangeQtyOrdered", qtyOrdered: qty, productId: product.id });
            },
            onUnitOfMeasureChanged: (uom: string) => {
                dispatch({ type: "Pages/ProductList/ChangeUnitOfMeasure", unitOfMeasure: uom, productId: product.id });
            },
        };
    }, [selectedVariant, productContext, dispatch, product.id]);

    if (!isAuthenticated) {
        return (
            <StyledWrapper {...styles.signInWrapper}>
                {isVariantProduct && !isSelectionComplete && (
                    <AddToCartVariantParent {...styles.addToCartButton} onClick={() => history.push(productDetailPathWithVariant)} />
                )}
                <SignInButton extendedStyles={styles.signInButton} />
            </StyledWrapper>
        );
    }

    // Variant parent with no selection or incomplete selection - navigate to PDP
    if (isVariantProduct && (!isSelectionComplete || !selectedVariant)) {
        return <AddToCartVariantParent {...styles.addToCartButton} onClick={() => history.push(productDetailPathWithVariant)} />;
    }

    // Variant parent with complete selection - wrap in variant context so price/cart use correct variant ID
    // Note: Add to Wishlist is rendered by ProductListActions.addToListWrapper (next to Compare) for all products.
    if (isVariantProduct && variantProductContext) {
        return (
            <ProductContext.Provider value={variantProductContext}>
                <ProductQuantityOrdered labelOverride="" showActions={true} extendedActionStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${selectedVariant!.id}`}
                    extendedStyles={styles.addToCartButton}
                    variantSelectionCompleted={true}
                />
            </ProductContext.Provider>
        );
    }

    if (isNonPurchasable) {
        return (
            <>
            {/* Xng-changes : show QuantityOrdered for web-only */}
            <ProductQuantityOrdered labelOverride="" showActions={true} extendedActionStyles={styles.quantityOrdered} />
            <ProductAddToListLink
                showAsButton={true}
                data-test-selector={`actionsAddToList${product.id}`}
                extendedStyles={styles.addToListLink}
            />
            </>
        );
    }

    return (
        <>
            <ProductQuantityOrdered labelOverride="" showActions={true} extendedActionStyles={styles.quantityOrdered} />
            <ProductAddToCartButton
                data-test-selector={`actionsAddToCart${product.id}`}
                extendedStyles={styles.addToCartButton}
            />
        </>
    );
};

export default AddToCartActionSection;
